import { useState, useEffect, useCallback } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { API } from "@/App";
import { toast } from "sonner";
import {
  Flag,
  LayoutDashboard,
  Building2,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  Sparkles,
  UserCog,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV_ITEMS = [
  { path: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { path: "/admin/clubs", label: "Clubes", icon: Building2 },
  { path: "/admin/courses", label: "Campos", icon: Flag },
  { path: "/admin/social", label: "Social", icon: Sparkles },
  { path: "/admin/players", label: "Usuarios", icon: UserCog },
  { path: "/admin/users", label: "Administradores", icon: Users },
  { path: "/admin/settings", label: "Configuracion", icon: Settings },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [adminUser, setAdminUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const verifyAdmin = useCallback(async () => {
    try {
      const stored = localStorage.getItem("cadii_admin_user");
      if (!stored) {
        navigate("/admin/login", { replace: true });
        return;
      }

      const res = await fetch(`${API}/admin/dashboard`, {
        credentials: "include",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("cadii_admin_token") || ""}`,
        },
      });

      if (!res.ok) {
        localStorage.removeItem("cadii_admin_user");
        localStorage.removeItem("cadii_admin_token");
        navigate("/admin/login", { replace: true });
        return;
      }

      setAdminUser(JSON.parse(stored));
    } catch {
      navigate("/admin/login", { replace: true });
    } finally {
      setChecking(false);
    }
  }, [navigate]);

  useEffect(() => {
    verifyAdmin();
  }, [verifyAdmin]);

  const handleLogout = () => {
    localStorage.removeItem("cadii_admin_user");
    localStorage.removeItem("cadii_admin_token");
    toast.success("Sesion cerrada");
    navigate("/admin/login", { replace: true });
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="w-10 h-10 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isActive = (item) =>
    item.exact
      ? location.pathname === item.path
      : location.pathname.startsWith(item.path);

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex" style={{ fontFamily: "Outfit, sans-serif" }}>
      {/* Sidebar — desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-gray-100 fixed inset-y-0 z-30">
        <div className="flex items-center gap-2.5 px-6 h-16 border-b border-gray-100">
          <Flag className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} />
          <span className="text-lg font-bold text-[#0F172A]">cadii</span>
          <span className="text-xs font-medium text-[#64748B] ml-auto bg-gray-100 px-2 py-0.5 rounded-full">House</span>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1" data-testid="admin-sidebar">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? "bg-[#1B4D3E]/10 text-[#1B4D3E]"
                    : "text-[#64748B] hover:bg-gray-50 hover:text-[#0F172A]"
                }`}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
              >
                <Icon className="w-5 h-5" strokeWidth={1.5} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-gray-100">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-[#1B4D3E] flex items-center justify-center text-white text-xs font-bold">
              {adminUser?.name?.[0] || "A"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#0F172A] truncate">{adminUser?.name}</p>
              <p className="text-xs text-[#64748B] truncate">{adminUser?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 w-full transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            Cerrar sesion
          </button>
        </div>
      </aside>

      {/* Mobile header + sidebar overlay */}
      <header className="lg:hidden fixed top-0 inset-x-0 h-14 bg-white border-b border-gray-100 z-40 flex items-center px-4 justify-between">
        <div className="flex items-center gap-2">
          <Flag className="w-5 h-5 text-[#1B4D3E]" />
          <span className="font-bold text-[#0F172A]">cadii</span>
          <span className="text-xs text-[#64748B] bg-gray-100 px-2 py-0.5 rounded-full">House</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="admin-menu-btn">
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </header>

      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-30 bg-black/40" onClick={() => setSidebarOpen(false)}>
          <aside
            className="w-64 bg-white h-full pt-16 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <nav className="px-3 py-4 space-y-1">
              {NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                const active = isActive(item);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      active
                        ? "bg-[#1B4D3E]/10 text-[#1B4D3E]"
                        : "text-[#64748B] hover:bg-gray-50 hover:text-[#0F172A]"
                    }`}
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
            <div className="p-3 border-t border-gray-100 mt-4">
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 w-full"
              >
                <LogOut className="w-5 h-5" strokeWidth={1.5} />
                Cerrar sesion
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0">
        <Outlet context={{ adminUser, setAdminUser }} />
      </main>
    </div>
  );
}
